import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { Ref } from '@common/a2files-g/file.model';
import { FileDbService } from '@common/a2files-g/services/file-db.service';
import { setValidationForm, setErrorMessageForm } from '@common/helpers/utils';
import { TranslateService } from '@ngx-translate/core';
import { ActionEvent } from '@shared/models/common.model';
import { OrganizationModel } from '@shared/models/organization.model';
import { ProjectModel } from '@shared/models/project.model';
import { DiscountRateModel } from '@shared/models/setting.model';
import { ExerciseService } from '@shared/services/exercise.service';
import { OrganizationService } from '@shared/services/organization.service';
import { ProjectService } from '@shared/services/project.service';
import { ConfirmationService } from 'primeng/api';
import { Dropdown } from 'primeng/dropdown';
import { AppService } from 'projects/admin/src/app/core/services/app.service';
import { BehaviorSubject, map, shareReplay, tap } from 'rxjs';

type Mode = 'create' | 'edit';

@Component({
  selector: 'app-basic-project-form',
  templateUrl: './basic-project-form.component.html',
  styleUrls: ['./basic-project-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasicProjectFormComponent implements OnInit, AfterViewInit {
  @ViewChild('dropdown') dropdown?: Dropdown;
  @Input() project: Partial<ProjectModel>;
  @Input() mode: Mode = 'create';
  @Output() action = new EventEmitter<ActionEvent>();

  organizations$ = this.appService.organizations$.pipe(map(organizations => organizations.map(OrganizationModel.genRef)));
  currencies$ = this.appService.currencies$;


  discountRates$ = this.appService.discountRates$;
  public readonly discountTypeKeys = DiscountRateModel.DiscountTypeKeys;
  discountRatesOptions$ = this.discountRates$.pipe(
    map(discountRates => {
      return this.discountTypeKeys.map(key => {
        const discountRate30 = discountRates.discountRate30.find(d => d.type == key).value;
        const discountRate75 = discountRates.discountRate75.find(d => d.type == key).value;
        return { type: key, discountRate30, discountRate75 }
      })
    })
  )

  public id = null;
  public get pathFiles() { return `projects/${this.id}/files/`; }
  public get refFiles() {
    return {
      col: 'projects',
      id: this.id,
      type: 'image'
    } as Ref
  }

  form: FormGroup = this.fb.group({
    name: [null, [Validators.required]],
    displayName: [null, [Validators.required]],
    baseline: [null, [Validators.required]],
    period: [null, [Validators.required]],
    organization: [null, [Validators.required]],
    isPublic: [false],
    summary: [null],
    discountType: [null, Validators.required],
    currency: [null, Validators.required]
  })

  validationMessages = {
    name: [{ type: 'required', message: 'VALIDATOR.PROJECT_NAME_REQUIRED' }],
    displayName: [{ type: 'required', message: 'VALIDATOR.PROJECT_SHORT_NAME_REQUIRED' }],
    baseline: [{ type: 'required', message: 'VALIDATOR.PROJECT_BASELINE_REQUIRED' }],
    period: [{ type: 'required', message: 'VALIDATOR.FIRST_EXERCISE_REQUIRED' }],
    organization: [{ type: 'required', message: 'VALIDATOR.ORGANIZATION_REQUIRED' }],
    discountType: [{ type: 'required', message: 'VALIDATOR.DISCOUNT_TYPE_REQUIRED' }],
    currency: [{ type: 'required', message: 'VALIDATOR.CURRENCY_REQUIRED' }],
  }

  isControlValid = setValidationForm(this.form);
  getErrorMessage = setErrorMessageForm(this.form, this.validationMessages);

  isCreating = false;

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectService,
    private organizationService: OrganizationService,
    private fileDbService: FileDbService,
    public appService: AppService,
    private exerciseService: ExerciseService,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService,
    ) { }

  private _filesPhoto: BehaviorSubject<any[]> = new BehaviorSubject([]);
  private filesPhoto$ = this._filesPhoto.asObservable();

  public filesPhotoToDisplay$ = this.filesPhoto$.pipe(
    map(files => files ?? []),
    shareReplay(1)
  );

  public filesPhotoCanUpload$ = this.filesPhotoToDisplay$.pipe(
    map(files => files?.length == 0),
  );

  private lastImageUrl: string = "";
  public filePhotoUrl$ = this.filesPhotoToDisplay$.pipe(
    map(files => files?.sort(s => s.createdAt)?.reverse()?.[0]?.url ?? null),
    tap(_url => this.lastImageUrl = _url)
  );

  ngOnInit(): void {
    this.form.reset();
    if (this.mode == 'edit') {
      // console.log("LLEGA", this.project)
      this.form.patchValue(this.project);
      this.id = this.project.id
      this.form.controls['period'].clearValidators();
      this.form.controls['period'].updateValueAndValidity();
      this.form.controls['period'].disable();

    }

    if (this.mode == 'create') {
      this.id = this.fileDbService.createId();
    }
    this.fileDbService.getByRef$("files", this.refFiles, 'file').subscribe(f => this._filesPhoto.next(f ?? []));
  }

  ngAfterViewInit(): void {
    if (this.dropdown) {
      (this.dropdown.filterBy as any) = {
        split: (_: any) => [(item: any) => item],
      };
    }
  }

  async onConfim(data) {
    this.appService.startLoading();
    await this.projectService.changeDiscounType({ project: { id: this.project.id }, discountType: data.discountType });
    await this.onSave(data);
    this.appService.stopLoading();
  }

  async onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      let data = this.form.value;
      if (this.project && this.project.discountType && this.project.discountType !== data.discountType) {
        this.confirmationService.confirm({
          message: this.translateService.instant('LABEL.CHANGE_DISCOUNT_TYPE_CONFIRMATION'),
          accept: () => {
            this.onConfim(data);
          }
        });
      } else {
        this.onSave(data);
      }
    }
  }

  async onSave(data) {

    if (this.lastImageUrl != this.project?.imageUrl) {
      data['imageUrl'] = this.lastImageUrl;
    }

    switch (this.mode) {
      case 'create':
        await this.create(data);
        break;
      case 'edit':
        await this.edit(data);
        break;
    }
  }

  async create(data) {
    this.isCreating = true;
    data.id = this.id;
    const id = await this.projectService.create(data, true);
    const exercise = await this.createExercise(data);

    this.form.reset();
    this.action.emit({
      type: 'create',
      payload: {
        id,
        exercise
      }
    })
    this.isCreating = false;
  }

  async createExercise(project) {
    const data = {
      project: project,
      period: project.period
    };
    return await this.exerciseService.create(data);
  }

  async edit(data) {
    data.id = this.project.id;
    const id = await this.projectService.update(data);
    this.form.reset();
    this.action.emit({
      type: 'edit',
      payload: {
        project: data
      }
    })
  }

  cancel() {
    // console.log("Cancel")
    this.action.emit({
      type: 'cancel',
      payload: null
    })
  }

  onDelete(project) {
    this.action.emit({
      type: 'delete',
      payload: { project }
    })
  }

  onNewOrganization() {
    // console.log("New organization")
    this.organizationService.addDialog();
  }
}
